import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Download } from "lucide-react";
import { MaskedLines, Overline } from "./Reveal";
import { API } from "@/lib/api";

const HERO_IMG = "/brand/masseria/hero-2.jpg";

export const Hero = ({ onCta }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative flex min-h-[100svh] items-end overflow-hidden bg-[#0E1A4A]"
      data-testid="hero-section"
    >
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 z-0">
        <img
          src={HERO_IMG}
          alt="Masseria in Puglia"
          className="h-full w-full object-cover"
        />
      </motion.div>

      {/* Navy institutional overlay */}
      <div className="absolute inset-0 z-10 bg-gradient-to-b from-[#0E1A4A]/85 via-[#16256B]/70 to-[#0E1A4A]" />
      <div className="absolute inset-0 z-10 bg-gradient-to-r from-[#0E1A4A]/90 to-transparent" />

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-20 mx-auto w-full max-w-7xl px-5 pb-10 pt-24 sm:px-8 sm:pb-14 sm:pt-28"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.6 }}
          className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#C9A227]/60 bg-[#C9A227]/15 px-3.5 py-1.5"
          data-testid="hero-urgency-badge"
        >
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#C9A227] opacity-75" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#C9A227]" />
          </span>
          <span className="font-body text-[11px] font-medium uppercase tracking-wide text-[#F0DC94] sm:text-xs">
            13&ndash;14 &amp; 27&ndash;28 Novembre 2026 &middot; Secolario &mdash; Masseria del Viverbene
          </span>
        </motion.div>

        <h1 className="max-w-5xl font-display text-[12vw] font-black uppercase leading-[0.9] tracking-tight text-white sm:text-7xl lg:text-[112px] xl:text-[128px]">
          <MaskedLines lines={["Visioni di Impresa."]} startDelay={0.25} />
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.7 }}
          className="mt-5 max-w-3xl font-display text-base font-bold uppercase leading-tight tracking-tight text-white/90 sm:text-xl lg:text-2xl"
        >
          Formare gli imprenditori di oggi.<br />
          Costruire quelli di domani.{" "}
          <span className="text-[#C9A227]">Adesso.</span>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.7 }}
          className="mt-5 max-w-2xl font-body text-sm leading-relaxed text-white/75 sm:text-[15px]"
        >
          Due week-end <strong className="text-white">residenziali di full immersion</strong> in masseria per <strong className="text-white">giovani imprenditori, aspiranti imprenditori, liberi professionisti e studenti universitari</strong>. Si lavora, si fa convivialità e networking e si cresce insieme &mdash; i rapporti tra colleghi diventano la prima risorsa d&rsquo;impresa.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.7 }}
          className="mt-6 flex flex-col items-start gap-3 sm:flex-row sm:items-center"
        >
          <button
            onClick={onCta}
            className="group inline-flex items-center gap-2.5 rounded-full bg-[#C9A227] px-6 py-3 font-display text-sm font-bold text-[#16256B] transition-transform duration-200 hover:scale-[1.04] active:scale-95 sm:text-base"
            data-testid="hero-cta-button"
          >
            Prenota il tuo posto
            <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
          </button>
          <a
            href={`${API}/programma.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/5 px-5 py-3 font-display text-sm font-bold text-white backdrop-blur-sm transition-colors duration-200 hover:border-white/60 hover:bg-white/10 sm:text-base"
            data-testid="hero-download-program"
          >
            <Download className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            Scarica il programma
          </a>
          <span className="font-body text-xs text-white/60 sm:text-sm">
            Secolario &mdash; Masseria del Viverbene &middot; Palmariggi (Le)
          </span>
        </motion.div>
      </motion.div>

      <div className="absolute bottom-24 right-6 z-20 hidden lg:block">
        <Overline className="text-white/70 [writing-mode:vertical-rl]">
          Masseria · Lecce · Puglia
        </Overline>
      </div>
    </section>
  );
};
